import {
  fetchWithCache,
  generateUserCacheKey,
  invalidateCache,
} from "@punchup/utils/caches";

import { Database } from "../../supabase.types";
import { SpecialRow } from "../../legacy/specials";
import { SupabaseClient } from "@supabase/supabase-js";
import dayjs from "@punchup/utils/dayjs";
import { getUser } from "../../supabase/supabase";
import { logException } from "@punchup/sentry";

export async function addSpecialToUser({
  supabaseAdmin,
  user_id,
  special_id,
}: {
  supabaseAdmin: SupabaseClient<any, "public", any>;
  user_id?: string;
  special_id?: string;
}) {
  if (!user_id) {
    throw new Error(
      "Unable to add purchased special to user because user_id is empty."
    );
  }

  if (!special_id) {
    throw new Error(
      "Unable to add purchased special to user because special_id is empty."
    );
  }

  const { error } = await supabaseAdmin
    .from("user_specials")
    .upsert(
      {
        user_id: user_id,
        special_id: special_id,
      },
      {
        onConflict: "user_id,special_id",
        ignoreDuplicates: true,
      }
    )
    .select();

  // Invalidate the user's paid special ids cache
  await invalidateCache(
    generateUserCacheKey(user_id, {
      paidSpecialIds: true,
    })
  );

  if (error) {
    logException(error, {
      tags: {
        user_id,
        special_id,
        function: "addSpecialToUser",
      },
    });
    throw error;
  }
}

export async function userHasPurchasedSpecial(
  supabase: SupabaseClient<Database>,
  special: SpecialRow
): Promise<boolean> {
  const user = await getUser(supabase);
  if (!user) {
    return false;
  }

  const { status, data, error } = await supabase
    .from("user_specials")
    .select("user_id")
    .eq("user_id", user.id)
    .eq("special_id", special.id)
    .maybeSingle();

  if (error) {
    console.error(error);
  } else if (status === 200) {
    if (data) {
      return true;
    } else {
      return false;
    }
  }
  throw new Error(`Error finding special of ${special.id} for user ${user.id}`);
}

// All temp below

export type SpecialRowWithCollectionId = SpecialRow & {
  collection_id: string | null;
};

// Keep the getUserPaidSpecials function signature with cursor instead of offset
async function getUserPaidSpecials(
  supabase: SupabaseClient<Database>,
  userId: string,
  cursor: number = 0,
  limit: number = 20,
  comedianId?: string
): Promise<SpecialRowWithCollectionId[]> {
  const [directSpecials, collectionSpecials] = await Promise.all([
    getUserDirectlyPurchasedSpecials(supabase, userId, comedianId),
    getUserCollectionBasedSpecials(supabase, userId, comedianId),
  ]);

  // Merge results and sort by published_at
  const allSpecials = [...directSpecials, ...collectionSpecials];

  // Sort by published_at date (most recent first), then by id for consistent ordering
  allSpecials.sort((a, b) => {
    const dateA = a.published_at ? dayjs(a.published_at).valueOf() : 0;
    const dateB = b.published_at ? dayjs(b.published_at).valueOf() : 0;

    if (dateB !== dateA) {
      return dateB - dateA;
    }

    // Secondary sort by id (string comparison for UUIDs)
    return b.id.localeCompare(a.id);
  });

  // Apply cursor-based pagination (cursor is really an offset)
  return allSpecials.slice(cursor, cursor + limit + 1); // +1 to detect next page
}

async function getUserPaidSpecialIdsFromCache(
  supabase: SupabaseClient<Database>,
  userId: string
): Promise<string[]> {
  const cacheKey = generateUserCacheKey(userId, {
    paidSpecialIds: true,
  });
  const fetchSpecialIds = async () => {
    const paidSpecials = await getUserPaidSpecials(supabase, userId, 0, 1000);
    return paidSpecials.map((special) => special.id);
  };
  return await fetchWithCache(cacheKey, fetchSpecialIds, 60 * 5); // 5 minutes
}

// Function to get directly purchased specials (from user_specials table)
export async function getUserDirectlyPurchasedSpecials(
  supabase: SupabaseClient<Database>,
  userId: string,
  comedianId?: string
): Promise<(SpecialRow & { collection_id: null })[]> {
  let query = supabase
    .from("user_specials")
    .select(`*, special:specials!user_specials_special_id_fkey!inner(*)`)
    .eq("user_id", userId)
    .eq("special.is_live", true)
    .lte("special.published_at", dayjs().toISOString());

  // Filter by comedianId if provided
  if (comedianId) {
    query = query.eq("special.comedian_id", comedianId);
  }

  const { data: specials, error } = await query;

  if (error) {
    throw error;
  }

  return specials.map(({ special }) => ({
    ...special,
    collection_id: null,
  }));
}

// Function to get collection-based specials (from user_paid_collections)
export async function getUserCollectionBasedSpecials(
  supabase: SupabaseClient<Database>,
  userId: string,
  comedianId?: string
): Promise<(SpecialRow & { collection_id: string })[]> {
  let query = supabase
    .from("user_paid_collections")
    .select(
      `
        collection_id,
        collections!inner(
          collection_specials!inner(
            specials!inner(*)
          )
        )
      `
    )
    .eq("user_id", userId)
    .eq("collections.collection_specials.specials.is_live", true)
    .lte(
      "collections.collection_specials.specials.published_at",
      dayjs().toISOString()
    );

  // Filter by comedianId if provided
  if (comedianId) {
    query = query.eq(
      "collections.collection_specials.specials.comedian_id",
      comedianId
    );
  }

  const { data: collections, error } = await query;

  if (error) {
    throw error;
  }

  // Flatten the nested structure
  const specials: (SpecialRow & { collection_id: string })[] = [];

  collections?.forEach((collection) => {
    collection.collections.collection_specials?.forEach((collectionSpecial) => {
      if (collectionSpecial.specials) {
        specials.push({
          ...collectionSpecial.specials,
          collection_id: collection.collection_id,
        });
      }
    });
  });

  return specials;
}
